import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';

const STATUS_LABELS = {
  updated: { text: 'Updated', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  employee_not_found: { text: 'Code not found', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  invalid_email: { text: 'Invalid email', cls: 'bg-red-50 text-red-700 border-red-200' },
  unparseable: { text: 'Could not parse', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function ResultsList({ result }) {
  const items = result.results || [];
  const rows = [];
  for (let i = 0; i < items.length; i++) {
    const r = items[i];
    const s = STATUS_LABELS[r.status] || STATUS_LABELS.unparseable;
    rows.push(
      <div key={i} className="flex items-center justify-between text-xs border rounded px-2 py-1">
        <span className="truncate mr-2">
          <span className="font-mono">{r.code || r.line}</span>
          {r.email && <span className="text-muted-foreground ml-2">{r.email}</span>}
          {r.name && <span className="text-muted-foreground ml-2">({r.name})</span>}
        </span>
        <Badge variant="outline" className={'text-[10px] shrink-0 ' + s.cls}>{s.text}</Badge>
      </div>
    );
  }
  return (
    <div className="max-h-64 overflow-y-auto space-y-1" data-testid="bulk-email-results">
      {rows}
      <p className="text-xs text-muted-foreground pt-1">
        {result.updated} of {result.total_lines} line(s) applied.
      </p>
    </div>
  );
}

export default function BulkEmailImportDialog({ open, onOpenChange, onImported }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    setBusy(true);
    try {
      const res = await api.post('/employees/bulk-email', { text });
      setResult(res.data);
      if (res.data.updated > 0) {
        toast.success(`${res.data.updated} email(s) updated`);
        onImported();
      } else {
        toast.error('No emails were updated — check the results below');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  const reset = (v) => {
    if (!v) { setText(''); setResult(null); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />Bulk Email Import
          </DialogTitle>
          <DialogDescription>
            Paste one line per employee: <code className="text-xs bg-muted px-1 rounded">code, email</code> — separators like comma, tab, colon or spaces all work.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={8}
            placeholder={'1301, lovely.garg@blogic.in\n1158  sourav.kapil@blogic.in\n204: priyanju@blogic.in'}
            className="font-mono text-xs"
            data-testid="bulk-email-textarea"
          />
        ) : (
          <ResultsList result={result} />
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => reset(false)}>Cancel</Button>
              <Button onClick={handleImport} disabled={busy || !text.trim()} data-testid="bulk-email-import-btn">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Import Emails
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setResult(null); }} data-testid="bulk-email-again-btn">Import More</Button>
              <Button onClick={() => reset(false)}>Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
